import React, { useMemo } from 'react';
import SpotlightSearch from '@/modules/Search/components/widget/SpotlightSearch';
import Footer from '@common/Layout/Footer';
import Header from '@common/Layout/Header';
import type {
  LayoutQuery,
  NavigationItem,
  PageAccessibilityBar,
  PageAccessibilityButton,
  PageAuthenticationBar,
  PageCorporateLogosBlock,
  PageFooterBar,
  PageHeaderBar,
  PageHeaderOrganizationBar,
  PageNavigationBar,
  PageSearchBlock,
} from '@common/Providers/GraphqlProvider/generated';
import {
  useLayoutQuery,
  useNavigationTreeQuery,
  usePageAccessibilityBarQuery,
} from '@common/Providers/GraphqlProvider/generated';
import { useModalSearchStore } from '@utils/store/store';
import AccessibilityButton2 from '@views/AccessibilityButton/AccessibilityButton';

import AccessibilityButtonMinimal from '../Accessibility/AccesibilityButtonMinimal';
import AccessibilityButton from '../Accessibility/AccessibilityButton';
import WrapperGrayScale from '../Accessibility/WrapperGrayScale';
import ModalDialog from '../Modals/ModalDialog';
import ModalLoading from '../Modals/ModalLoading';
import type { IAccessibility } from '../Providers/AccessibilityProvider/AccessibilityProvider';
import AccessibilityProvider from '../Providers/AccessibilityProvider/AccessibilityProvider';
import LayoutProvider from './LayoutProvider';

type Props = {
  children: React.ReactNode;
};

type Typename =
  | 'PageAccessibilityBar'
  | 'PageAuthenticationBar'
  | 'PageHeaderBar'
  | 'PageNavigationBar'
  | 'PageFooterBar'
  | 'PageCorporateLogosBlock'
  | 'PageHeaderOrganizationBar'
  | 'PageAccessibilityButton'
  | 'PageSearchBlock'
  | undefined;

const Layout = ({ children }: Props) => {
  const { data, loading: isLayoutLoading } = useLayoutQuery();
  const { show } = useModalSearchStore();

  const getBlockHeader = (blockName: Typename, data: LayoutQuery) => {
    return data.header.find((item) => item.__typename === blockName);
  };

  const getBlockFooter = (blockName: Typename, data: LayoutQuery) => {
    return data.footer.find((item) => item.__typename === blockName);
  };

  const searchBlock = useMemo(() => {
    if (data) return getBlockHeader('PageSearchBlock', data) as PageSearchBlock;
    return undefined;
  }, [data]);

  const authenticationBar = useMemo(() => {
    if (data) return getBlockHeader('PageAuthenticationBar', data) as PageAuthenticationBar;
    return undefined;
  }, [data]);

  const accessibilityBar = useMemo(() => {
    if (data) return getBlockHeader('PageAccessibilityBar', data) as PageAccessibilityBar;
    return undefined;
  }, [data]);

  const headerBar = useMemo(() => {
    if (data) return getBlockHeader('PageHeaderBar', data) as PageHeaderBar;
    return undefined;
  }, [data]);

  const headerOrganizationBar = useMemo(() => {
    if (data) return getBlockHeader('PageHeaderOrganizationBar', data) as PageHeaderOrganizationBar;
    return undefined;
  }, [data]);

  const navigationBar = useMemo(() => {
    if (data) return getBlockHeader('PageNavigationBar', data) as PageNavigationBar;
    return undefined;
  }, [data]);

  // Fetch navigation tree in a single request (eliminates N+1 queries)
  // Fetch navigation tree in a single request (eliminates N+1 queries)
  const { data: navigationTreeData, loading: isNavigationTreeLoading } = useNavigationTreeQuery({
    variables: {
      navigationBarId: navigationBar?.id || '',
      maxDepth: null, // Fetch all levels
    },
    skip: !navigationBar?.id, // Skip if no navigationBar ID
  });

  const isNavigationLoading = isLayoutLoading || isNavigationTreeLoading;

  const navigationItems = useMemo(() => {
    return (navigationTreeData?.navigationTree || []) as NavigationItem[];
  }, [navigationTreeData]);

  const pageFooterBar = useMemo(() => {
    if (data) return getBlockFooter('PageFooterBar', data) as PageFooterBar;
    return undefined;
  }, [data]);

  const pageCorporateLogosBlock = useMemo(() => {
    if (data) return getBlockFooter('PageCorporateLogosBlock', data) as PageCorporateLogosBlock;
    return undefined;
  }, [data]);

  const pageAccessibilityButton = useMemo(() => {
    if (data) return getBlockHeader('PageAccessibilityButton', data) as PageAccessibilityButton;
    return undefined;
  }, [data]);

  const layoutParameters: { isGrayScaled: boolean; isHightContrast: boolean } =
    localStorage.getItem('layoutParameters')
      ? JSON.parse(localStorage.getItem('layoutParameters') || '')
      : { isGrayScaled: false, isHightContrast: false };

  const accessibilityParameters: IAccessibility = localStorage.getItem('accessibilityParameters')
    ? JSON.parse(localStorage.getItem('accessibilityParameters') || '')
    : {
        storytellerEnabled: false,
        fontSize: 0,
        isCursorBig: false,
        isDislexic: false,
        isHightlightLink: false,
        langTeller: 'es-ES',
        languaje: 'es',
      };

  const { data: pageStyle } = usePageAccessibilityBarQuery();

  const pageStyleFound = pageStyle?.footer.find((item) => {
    return item.__typename === 'PageAccessibilityBar';
  }) as PageAccessibilityBar;

  return (
    <LayoutProvider
      value={{
        layout: {
          navigationBar,
          navigationItems,
          isNavigationLoading,
          accessibilityBar,
          authenticationBar,
          headerBar,
          searchBlock,
          pageFooterBar,
          pageCorporateLogosBlock,
          headerOrganizationBar,
          isGrayScaled: layoutParameters.isGrayScaled,
          isHightContrast: layoutParameters.isHightContrast,
        },
      }}
    >
      <AccessibilityProvider value={{ ...accessibilityParameters }}>
        <Header />
        <WrapperGrayScale>
          {children}
          <Footer />
        </WrapperGrayScale>
        <ModalDialog />
        <ModalLoading />
        {pageStyleFound?.pageBlockStyle === 'A' ? (
          <AccessibilityButton />
        ) : pageStyleFound?.pageBlockStyle === 'B' ? (
          <AccessibilityButtonMinimal />
        ) : null}
        {pageAccessibilityButton?.isDisabled !== true && (
          <AccessibilityButton2 accessibilityButton={pageAccessibilityButton} />
        )}
        {searchBlock && !searchBlock.isDisabled && (
          <SpotlightSearch open={show} searchBlock={searchBlock} />
        )}
      </AccessibilityProvider>
    </LayoutProvider>
  );
};

export default Layout;
